import { useState, useEffect, useCallback } from 'react';
import { type Consultation } from '../../types';

interface Props {
  customerId: string;
}

interface CustomerBasicInfo {
  assignedStaffIdx: number;
}

interface ConsultationApiItem {
  consultationIdx: number;
  type: string;
  title: string;
  startDt: string;
  endDt: string;
  status: string;
  memo?: string;
}

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

const addOneHour = (dateTime: string) => {
  const d = new Date(dateTime);
  d.setHours(d.getHours() + 1);
  return d.toISOString();
};

const mapConsultTypeToApi = (uiType: string) => {
  if (uiType === '전화') return 'CALL_CONSULT';
  return 'VISIT_CONSULT';
};

const mapConsultTypeToUi = (apiType: string) => {
  if (apiType === 'CALL_CONSULT') return '전화';
  return '대면';
};

const parseJsonSafe = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export default function ConsultationsTab({ customerId }: Props) {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [editingConsultationId, setEditingConsultationId] = useState<string | null>(null);
  const [basicInfo, setBasicInfo] = useState<CustomerBasicInfo>({ assignedStaffIdx: 0 });
  const [newConsultation, setNewConsultation] = useState({
    date: formatDate(new Date()),
    time: '10:00',
    type: '대면',
    category: '일반상담',
    content: ''
  });

  const fetchCustomerBasicInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=OVERVIEW`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);
      if (result?.status && result.data?.basicInfo) {
        setBasicInfo({
          assignedStaffIdx: Number(result.data.basicInfo.assignedStaffIdx || 0)
        });
      }
    } catch (error) {
      console.error('Failed to fetch customer basic info:', error);
    }
  }, [customerId]);

  const fetchConsultations = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/consultation?page=1&size=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);

      const items: ConsultationApiItem[] =
        (Array.isArray(result?.data?.items) && result.data.items) ||
        (Array.isArray(result?.items) && result.items) ||
        [];

      setConsultations(items.map((item) => {
          const sourceDate = item.startDt;
          const dateObj = new Date(sourceDate);
          return {
            id: String(item.consultationIdx || 0),
            date: Number.isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleDateString(),
            time: Number.isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            type: mapConsultTypeToUi(item.type || ''),
            category: item.title || '일반상담',
            content: item.memo || '-'
          };
        }));
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
      setConsultations([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerBasicInfo();
    fetchConsultations();
  }, [fetchCustomerBasicInfo, fetchConsultations]);

  const handleAddConsultation = async () => {
    if (!newConsultation.date || !newConsultation.time || !newConsultation.content.trim()) {
      alert('일시와 상담 내용을 입력해주세요.');
      return;
    }

    const startDt = new Date(`${newConsultation.date}T${newConsultation.time}:00`);
    if (Number.isNaN(startDt.getTime())) {
      alert('유효한 날짜/시간을 입력해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const payload: Record<string, any> = {
        type: mapConsultTypeToApi(newConsultation.type),
        title: newConsultation.category,
        startDt: startDt.toISOString(),
        endDt: addOneHour(startDt.toISOString()),
        assignedStaffIdx: basicInfo.assignedStaffIdx,
        memo: newConsultation.content
      };
      if (!editingConsultationId) {
        payload.status = 'SCHEDULED';
      }

      const url = editingConsultationId
        ? `https://api.wedmin.biz/customer/${customerId}/consultation/${editingConsultationId}`
        : `https://api.wedmin.biz/customer/${customerId}/consultation`;

      const response = await fetch(url, {
        method: editingConsultationId ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(result?.message || '상담내역 저장에 실패했습니다.');
      }

      alert(editingConsultationId ? '상담내역이 수정되었습니다.' : '상담내역이 등록되었습니다.');
      setShowConsultationForm(false);
      setEditingConsultationId(null);
      setNewConsultation({
        date: formatDate(new Date()),
        time: '10:00',
        type: '대면',
        category: '일반상담',
        content: ''
      });
      fetchConsultations();
    } catch (error: any) {
      console.error('Create consultation error:', error);
      alert(error?.message || '상담내역 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConsultation = async (id: string) => {
    if (!window.confirm('이 상담내역을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/consultation/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        let message = '삭제에 실패했습니다.';
        try {
          const json = await response.json();
          message = json.message || message;
        } catch {
          // no-op
        }
        throw new Error(message);
      }

      alert('삭제되었습니다.');
      fetchConsultations();
    } catch (error: any) {
      console.error('Delete consultation error:', error);
      alert(error?.message || '상담내역 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleEditConsultation = async (id: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/consultation/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);
      const item = result?.data || result;
      if (!item?.consultationIdx) throw new Error('상담내역 상세를 불러올 수 없습니다.');

      const dt = new Date(item.startDt);
      if (Number.isNaN(dt.getTime())) throw new Error('상담내역 날짜 형식이 올바르지 않습니다.');

      setEditingConsultationId(String(item.consultationIdx));
      setNewConsultation({
        date: formatDate(dt),
        time: dt.toTimeString().slice(0, 5),
        type: mapConsultTypeToUi(item.type || ''),
        category: item.title || '일반상담',
        content: item.memo || ''
      });
      setShowConsultationForm(true);
    } catch (error: any) {
      console.error('Get consultation detail error:', error);
      alert(error?.message || '상담내역을 불러오는 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">상담내역</h3>
          <button
            onClick={() => {
              setEditingConsultationId(null);
              setShowConsultationForm((prev) => !prev);
            }}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer flex items-center"
          >
            <i className="ri-add-line mr-2"></i>
            상담 등록
          </button>
        </div>

        {showConsultationForm && (
          <div className="p-6 border-b border-gray-100 bg-teal-50/40">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">날짜</label>
                <input
                  type="date"
                  value={newConsultation.date}
                  onChange={(e) => setNewConsultation((prev) => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">시간</label>
                <input
                  type="time"
                  value={newConsultation.time}
                  onChange={(e) => setNewConsultation((prev) => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">상담 방식</label>
                <select
                  value={newConsultation.type}
                  onChange={(e) => setNewConsultation((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="대면">대면</option>
                  <option value="전화">전화</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">유형</label>
                <input
                  type="text"
                  value={newConsultation.category}
                  onChange={(e) => setNewConsultation((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="예: 일반상담"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">상담 내용</label>
                <textarea
                  value={newConsultation.content}
                  onChange={(e) => setNewConsultation((prev) => ({ ...prev, content: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="상담 내용을 입력하세요"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowConsultationForm(false);
                  setEditingConsultationId(null);
                }}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleAddConsultation}
                disabled={isSaving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60"
              >
                {isSaving ? (editingConsultationId ? '수정 중...' : '등록 중...') : (editingConsultationId ? '수정' : '등록')}
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">불러오는 중...</div>
          ) : consultations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">등록된 상담내역이 없습니다.</div>
          ) : (
            consultations.map((consultation) => (
              <div key={consultation.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                      {consultation.type}
                    </span>
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                      {consultation.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {consultation.date} {consultation.time}
                    </span>
                    <button
                      onClick={() => handleEditConsultation(consultation.id)}
                      className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => handleDeleteConsultation(consultation.id)}
                      className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{consultation.content}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
